import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	HostListener,
	QueryList,
	ViewChildren,
	computed,
	effect,
	input,
	model,
	output,
	signal,
	untracked
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

const DIGIT = /^\d$/;
const DIGITS = /^\d+$/;
const NON_DIGITS = /\D/g;

@Component({
	selector: 'xp-otp-input',
	standalone: true,
	imports: [CommonModule, MatFormFieldModule, MatInputModule],
	templateUrl: './xp-otp-input.component.html',
	styleUrls: ['./xp-otp-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpOtpInputComponent {
	length = input(6);
	invalid = input(false);
	disabled = input(false);
	value = model('');
	completed = output<string>();

	slots = computed(() => Array.from({ length: this.length() }, (_, index) => index));

	private readonly typed = signal<string[]>([]);
	private lastReported = '';

	characters = computed(() => {
		const value = this.value();
		const typed = this.typed();
		const source = typed.join('') === value ? typed : [...value];
		return this.slots().map(index => source[index] ?? '');
	});

	@ViewChildren('box') private boxes!: QueryList<ElementRef<HTMLInputElement>>;

	constructor() {
		/* A wrong code clears the value from the screen while focus is still in the last box. */
		effect(() => {
			if (this.value() !== '') {
				return;
			}

			const stale = untracked(() => this.typed().some(digit => digit !== ''));
			if (!stale) {
				return;
			}

			untracked(() => {
				this.typed.set(this.slots().map(() => ''));
				this.lastReported = '';
			});
			queueMicrotask(() => this.focusFirstEmpty());
		}, { allowSignalWrites: true });
	}

	onInput(index: number): void {
		if (this.disabled()) {
			return;
		}

		const box = this.boxAt(index);
		if (box.value.length > 1 && this.fill(box.value)) {
			return;
		}

		if (!DIGIT.test(box.value)) {
			box.value = '';
			this.readBoxes();
			this.publish();
			return;
		}

		this.readBoxes();
		this.publish();
		this.focusAt(index + 1);
	}

	onKeyup(event: KeyboardEvent, index: number): void {
		if (this.disabled()) {
			return;
		}

		if (event.key === 'Backspace' || event.key === 'ArrowLeft') {
			this.focusAt(index - 1);
			return;
		}

		if (event.key === 'ArrowRight' || event.key === 'Enter') {
			this.focusAt(index + 1);
		}
	}

	@HostListener('paste', ['$event'])
	onPaste(event: ClipboardEvent): void {
		if (this.disabled()) {
			return;
		}

		event.preventDefault();
		this.fill(event.clipboardData?.getData('text/plain').replace(NON_DIGITS, '') ?? '');
	}

	private fill(code: string): boolean {
		if (code.length !== this.length() || !DIGITS.test(code)) {
			return false;
		}

		this.slots().forEach(index => {
			this.boxAt(index).value = code[index];
		});
		this.typed.set([...code]);
		this.value.set(code);
		this.publish();
		return true;
	}

	private publish(): void {
		const typed = this.typed();
		const complete = typed.length === this.length() && typed.every(digit => DIGIT.test(digit));
		if (!complete) {
			this.lastReported = '';
			return;
		}

		const code = typed.join('');
		if (code === this.lastReported) {
			return;
		}

		this.lastReported = code;
		this.completed.emit(code);
	}

	private focusFirstEmpty(): void {
		if (!this.boxes?.length || this.value() !== '') {
			return;
		}

		this.slots().forEach(index => {
			this.boxAt(index).value = '';
		});
		this.focusAt(0);
	}

	private readBoxes(): void {
		const typed = this.slots().map(index => this.boxAt(index).value);
		this.typed.set(typed);
		this.value.set(typed.join(''));
	}

	private focusAt(index: number): void {
		this.boxAt(Math.min(Math.max(index, 0), this.length() - 1)).focus();
	}

	private boxAt(index: number): HTMLInputElement {
		return this.boxes.get(index)!.nativeElement;
	}
}
